import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Home, GraduationCap, Calendar, MapPin, Clock, Users, Award, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BRAND = {
  name: 'ProTraining',
  fullName: 'Ox Resin ProTraining',
  tagline: 'Professional Installer Training',
  primaryColor: '#D35500',
  secondaryColor: '#381E0C',
  logo: 'https://customer-assets.emergentagent.com/job_d9177fe4-220c-4669-9998-86c6e454a508/artifacts/vm7nme87_IMG_8293.jpeg'
};

const courses = [
  {
    id: 'resin-bound',
    title: 'Resin Bound Installation',
    duration: '2 Days',
    location: 'Ox Resin Training Centre',
    description: 'Learn the complete resin bound installation process from base preparation to finishing touches.',
    features: [
      'Hands-on practical training',
      'Material mixing techniques',
      'Surface preparation methods',
      'Troubleshooting common issues',
      'Business & sales guidance'
    ],
    certification: 'Ox Resin Approved Installer',
    color: '#2F5E96'
  },
  {
    id: 'epoxy-floors',
    title: 'Epoxy Floor Systems',
    duration: '2 Days',
    location: 'Ox Resin Training Centre',
    description: 'Master epoxy floor coating techniques for industrial and decorative applications.',
    features: [
      'Surface assessment & prep',
      'Mixing & application methods',
      'Decorative finishes',
      'Safety procedures',
      'Quality control checks'
    ],
    certification: 'ProEpoxy Certified Installer',
    color: '#7A0B7A'
  },
  {
    id: 'decorative-walling',
    title: 'Decorative Walling',
    duration: '1 Day',
    location: 'Ox Resin Training Centre',
    description: 'Learn professional techniques for decorative wall coatings and finishes.',
    features: [
      'Wall preparation techniques',
      'Application methods',
      'Texture & pattern creation',
      'Color matching',
      'Maintenance guidance'
    ],
    certification: 'ProDecor Certified Installer',
    color: '#D5AB09'
  }
];

const ProTrainingHub = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEmbedMode = searchParams.get('embed') === 'true';
  const { user, isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-[#E8ECEF]">
      {/* Header */}
      {!isEmbedMode && (
        <header className="bg-white shadow-sm" style={{ borderBottom: `4px solid ${BRAND.primaryColor}` }}>
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-2 text-gray-600 hover:bg-gray-100"
            >
              <Home className="w-4 h-4 mr-2" />
              All Services
            </Button>
            <div className="flex items-center gap-4">
              {isAuthenticated && (
                <span className="hidden sm:block text-sm text-gray-600">{user?.name}</span>
              )}
              <img 
                src={BRAND.logo} 
                alt={BRAND.fullName}
                className="h-12 md:h-14"
              />
            </div>
          </div>
        </header>
      )}

      {/* Hero Section */}
      <div 
        className="text-white py-12 md:py-16"
        style={{ backgroundColor: BRAND.primaryColor }}
      >
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-white rounded-2xl p-3">
              <img 
                src={BRAND.logo} 
                alt={BRAND.fullName}
                className="h-16 md:h-20"
              />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            {BRAND.fullName}
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Become an Ox Resin Approved Installer with our comprehensive training courses
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12">
          {[
            { icon: GraduationCap, title: 'Expert Trainers', desc: 'Learn from industry professionals' },
            { icon: Award, title: 'Certification', desc: 'Become an approved installer' },
            { icon: Users, title: 'Small Groups', desc: 'Personalised attention' },
            { icon: CheckCircle, title: 'Hands-On', desc: 'Practical experience' }
          ].map((benefit) => (
            <div key={benefit.title} className="bg-white rounded-xl p-4 text-center shadow-sm">
              <benefit.icon className="w-8 h-8 mx-auto mb-2" style={{ color: BRAND.primaryColor }} />
              <h3 className="font-bold text-[#27292E]">{benefit.title}</h3>
              <p className="text-sm text-gray-500">{benefit.desc}</p>
            </div>
          ))}
        </div>

        {/* Course Cards */}
        <h2 className="text-2xl font-bold text-[#27292E] mb-6 text-center">Available Courses</h2>
        <div className="space-y-6">
          {courses.map((course) => (
            <div 
              key={course.id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden"
            >
              <div 
                className="h-2"
                style={{ backgroundColor: course.color }}
              />
              <div className="p-6">
                <div className="flex flex-col md:flex-row md:items-start gap-6">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#27292E] mb-2">{course.title}</h3>
                    <p className="text-gray-600 mb-4">{course.description}</p>
                    
                    <div className="flex flex-wrap gap-4 mb-4 text-sm">
                      <span className="flex items-center gap-1 text-gray-500">
                        <Clock className="w-4 h-4" />
                        {course.duration}
                      </span>
                      <span className="flex items-center gap-1 text-gray-500">
                        <MapPin className="w-4 h-4" />
                        {course.location}
                      </span>
                      <span className="flex items-center gap-1" style={{ color: course.color }}>
                        <Award className="w-4 h-4" />
                        {course.certification}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {course.features.map((feature) => (
                        <span 
                          key={feature}
                          className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    <Button
                      className="text-white px-6"
                      style={{ backgroundColor: course.color }}
                      onClick={() => window.open('mailto:training@oxresin.co.uk?subject=Training Enquiry: ' + course.title, '_blank')}
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Enquire Now
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div 
          className="mt-12 rounded-2xl p-8 text-center text-white"
          style={{ backgroundColor: BRAND.secondaryColor }}
        >
          <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Contact us to book your training course or to learn more about becoming an Ox Resin approved installer.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => window.open('mailto:training@oxresin.co.uk', '_blank')}
              className="bg-white hover:bg-gray-100"
              style={{ color: BRAND.primaryColor }}
            >
              training@oxresin.co.uk
            </Button>
            <Button
              onClick={() => window.open('tel:+441onal', '_blank')}
              variant="outline"
              className="border-white text-white hover:bg-white/10"
            >
              Call Us
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProTrainingHub;
